<script setup lang="ts">
import type { Comment, Media } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useElementVisibility } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, inject, ref, toRef, useTemplateRef, watchEffect } from "vue"
import { z } from "zod"
import { useListCommentsV1, usePostComment } from "@/api"
import { CommentDeleteModal, CommentTile } from "@/components"
import { Button, Drawer, Icon, Textarea } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MediaType } from "@/models"
import { EventVaultIdKey } from "@/providers/keys"
import { CommentDeleteModalKey, CommentsDrawerKey } from "@/symbols"
import { ContentType } from "@/types"
import { useToast } from "../ui/ToastReka/useToast"

interface CommentsDrawerProps {
  media: Media
  isActive?: boolean
}

const props = withDefaults(defineProps<CommentsDrawerProps>(), {
  isActive: false,
})

const emit = defineEmits<{
  (e: "flag", comment: Comment): void
}>()

const { t } = useTranslation()
const toast = useToast()

const eventVaultId = inject(EventVaultIdKey, undefined)
const media = toRef(props, "media")
const scrollEndRef = useTemplateRef<HTMLSpanElement | null>("scrollEndRef")
const isScrollEndVisible = useElementVisibility(scrollEndRef)

const { toggle: toggleCommentDeleteModal } = useModal(CommentDeleteModalKey)

const selectedCommentId = ref<number | undefined>()

function initiateDelete(comment?: Comment) {
  selectedCommentId.value = comment?.id
  toggleCommentDeleteModal()
}

function initiateFlag(comment: Comment) {
  emit("flag", comment)
}

const validationSchema = toTypedSchema(
  z.object({
    content: z.string().max(3000),
  }),
)

const { handleSubmit, handleReset, values } = useForm({ validationSchema })

const { mutate: mutatePostComment, isPending: isLoading } = usePostComment()

const client = useQueryClient()

const onSubmitComment = handleSubmit(() => {
  if (!eventVaultId) {
    toast.error()
    return
  }

  mutatePostComment({ eventVaultId, commentRequest: {
    contentId: media.value.id!,
    contentType: media.value?.type === MediaType.Vid ? ContentType.Video : ContentType.Photo,
    content: values.content ?? "",
  } }, {
    onSuccess: async () => {
      handleReset()
      await client.invalidateQueries({ queryKey: ["listComments", eventVaultId, media] })
      await client.invalidateQueries({ queryKey: ["listMedia", eventVaultId] })
    },
    onError: () => toast.error(),
  })
})

const isModalActive = computed(() => props.isActive)

const { commentItems, fetchNextPage, isFetching, hasNextPage, isLoading: isLoadingComments } = useListCommentsV1(eventVaultId, media, isModalActive, { perPage: 5 })

const canFetchMore = computed(() => !isLoadingComments.value && commentItems.value?.length && hasNextPage?.value)

function isLastComment(index: number) {
  return index === commentItems.value.length - 1
}

watchEffect(() => {
  if (canFetchMore.value && isScrollEndVisible.value && !isFetching.value)
    fetchNextPage()
})
</script>

<template>
  <Drawer :name="CommentsDrawerKey" :title="t('comment_drawer.title')" title-icon="ChatText">
    <transition name="gpx-fade" mode="out-in">
      <div v-if="isLoadingComments" class="flex flex-col gap-2 md:gap-4 mb-8">
        <CommentTile v-for="n in 3" :key="`skeleton-${n}`" is-loading />
      </div>

      <div v-else-if="!commentItems.length" class="font-montserrat text-sm md:text-base">
        {{ t("comment_drawer.no_comments") }}
      </div>
      <div v-else class="flex flex-col gap-2 md:gap-4 mb-8">
        <div v-for="(comment, index) in commentItems" :key="comment.id">
          <CommentTile :comment="comment" @delete="initiateDelete" @flag="initiateFlag" />
          <hr v-if="!isLastComment(index)" class="mt-2 md:mt-4">
        </div>
        <span ref="scrollEndRef" />
      </div>
    </transition>

    <div v-if="isFetching && !isLoadingComments" class="flex font-montserrat items-center gap-2 mb-6">
      <Icon icon="Loading" />
      {{ t("comment_drawer.is_loading") }}
    </div>

    <template #footer>
      <form @submit.prevent="onSubmitComment">
        <div class="bg-primary-10 px-6 py-2 md:py-4 border-t border-slate-300">
          <h3 class="font-comfortaa hidden md:block text-primary mb-3 text-sm font-semibold tracking-wide">
            {{ t("comment_drawer.leave_comment") }}
          </h3>
          <div class="flex gap-2">
            <div class="grow">
              <Textarea compact name="content" :label="t('comment_drawer.leave_comment')" :disabled="isLoading" />
            </div>
            <Button prefix-icon="PaperPlane" :disabled="isLoading || !values.content" class="self-end w-fit" />
          </div>
        </div>
      </form>
    </template>
  </Drawer>

  <CommentDeleteModal v-if="selectedCommentId" :comment-id="selectedCommentId" />
</template>
